// import styled from 'styled-components';
// import Link from 'next/link';
// import { useRouter } from 'next/router';
import Head from 'next/head';
import NoSections from '@features/PageBuilder/components/NoSections';
import { getLayout } from '@layouts/DefaultLayout';
import Sections from '@features/PageBuilder/components/Sections';
import Error from '@components/Error';
import Section from '@components/Section';
import NotFound from '@components/NotFound';
import SEO from '../features/WordPress/components/SEO';
import { GetStaticPaths, GetStaticProps } from 'next';
import {
  GeneralOptions,
  Page as IPage,
  SEO as ISEO,
  Sections as ISections,
  Template,
} from '@shared/types';

import path from 'path';
import fs from 'fs';

import fetchPageWithSiteData from '@features/WordPress/graphql/handlers/fetch-page-with-site-data';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

// Static paths from cache instead of fetching live
export const getStaticPaths: GetStaticPaths = async () => {
  const filePath = path.resolve(process.cwd(), 'data', 'all-pages.json');
  let pages: { uri: string; locale: string }[] = [];

  try {
    const fileContents = fs.readFileSync(filePath, 'utf-8');
    pages = JSON.parse(fileContents);
  } catch (err) {
    console.error('Failed to load all-pages.json:', err);
    return { paths: [], fallback: 'blocking' };
  }

  const validLocales = ['da', 'se', 'no', 'de'];
  const paths = pages.map(({ uri, locale }) => {
    const safeLocale = validLocales.includes(locale) ? locale : 'da';

    return {
      params: {
        slug: uri
          .split('/')
          .filter(Boolean)
          .filter((part) => part !== safeLocale),
      },
      locale: safeLocale,
    };
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<any, { slug: string[] }> = async ({
  locale,
  params,
}) => {
  let debug = false;
  let pathStr = params?.slug?.join('/').replace('x-debug', '') || '';

  if (params?.slug && params?.slug[params?.slug.length - 1] === 'x-debug') {
    debug = true;
  }

  if (!pathStr) {
    pathStr = `frontpage-${locale}`;
  }

  const fullCachePath = path.join(process.cwd(), 'data', 'full-pages.json');
  let fullPages: any[] = [];

  try {
    if (fs.existsSync(fullCachePath)) {
      const fileContents = fs.readFileSync(fullCachePath, 'utf-8');
      fullPages = JSON.parse(fileContents);
    }
  } catch (err) {
    console.warn('No full-pages.json cache found. Falling back to live fetch.');
  }

  const fallbackLocale = 'da';
  const activeLocale = locale || fallbackLocale;

  let cachedPage: any = null;

  if (fullPages.length > 0) {
    cachedPage = fullPages.find(
      (entry) =>
        entry.uri.replace(/^\/|\/$/g, '') === pathStr &&
        entry.locale === activeLocale
    );
  }

  if (!cachedPage || !cachedPage.data?.page) {
    console.warn('No matching page in cache for:', pathStr, activeLocale);
    try {
      const { data, errors } = await fetchPageWithSiteData(
        pathStr,
        activeLocale,
        false,
        debug
      );

      if (!data || !data.page) {
        console.error('GraphQL returned empty page data');
        return {
          notFound: true,
        };
      }

      // Fill in fallback data
      cachedPage = {
        uri: pathStr,
        locale: activeLocale,
        data,
      };
    } catch (err) {
      console.error('Error for page:', pathStr);
      console.error('GraphQL fetch failed:', err);
      return { notFound: true };
    }
  }

  const { page, generalOptions } = cachedPage.data;

  const isNotFound = !page || !page.postType;

  return {
    props: {
      errorCode: isNotFound ? 404 : null,
      page,

      ...(await serverSideTranslations(locale || '', [
        'account',
        'campaign',
        'checkout',
        'common',
        'pip',
        'redeem',
        'website',
      ])),

      generalOptions,
      path: pathStr,
      locale,
    },
    notFound: isNotFound,
    revalidate: 1,
  };
};

interface Props {
  errorCode: number | null;
  page: IPage;
  generalOptions: GeneralOptions;
  path: string;
  locale: string | undefined;
  seo: ISEO;
  template: Template;
  sections: ISections;
  errors: any;
}

const Page = (props: Props) => {
  const { sections, locale, seo, template, errors, errorCode, page } = props;

  const { id: templateId } = template || {};

  if (errorCode === 404) {
    return (
      <>
        <Head>
          <meta key="robots" name="robots" content="noindex" />
          <meta key="googlebot" name="googlebot" content="noindex" />
          <meta key="status" httpEquiv="Status" content="404 Not Found" />
        </Head>
        <NotFound />
      </>
    );
  }

  if (errors) {
    return <Error errors={errors} />;
  }

  if (!sections) {
    return (
      <Section>
        <div>No sections added to this page</div>
      </Section>
    );
  }

  return (
    <>
      <SEO locale={locale} translations={page?.translations} {...seo} />
      {sections && sections.sections && sections.sections.length > 0 ? (
        <Sections
          breadcrumbs={seo?.breadcrumbs}
          template={templateId}
          components={sections?.sections}
          locale={locale}
        />
      ) : (
        <>
          <NoSections />
        </>
      )}
    </>
  );
};

Page.getLayout = getLayout;

export default Page;
